import React from 'react';
import { MessageBar } from 'office-ui-fabric-react/lib/MessageBar';
import { Text } from 'office-ui-fabric-react/lib/Text';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import { formatDateString } from '../../utils';
import styles from './ContractDetailsMessageBar.style';

const getStatusMessage = (
  contractStatus,
  isReviewer,
  hasContractCreatePermission,
  submitter,
  reviewer,
  isOnboardedContract,
  lastUpdatedBy,
) => {
  switch (contractStatus) {
    case 'PendingInitialApproval':
      if (isReviewer) {
        return {
          isVisible: true,
          title: <Text className={styles.thisIsADraftTitle}>Action required</Text>,
          content: (
            <Text className={styles.thisIsADraftMessage}>
              <strong>{submitter}</strong> submitted contract for approval. Please review changes.
            </Text>
          ),
        };
      }
      return {
        isVisible: true,
        title: <Text className={styles.thisIsADraftTitle}>Contract pending approval</Text>,
        content: (
          <Text className={styles.thisIsADraftMessage}>
            This contract is pending approval by <strong>{reviewer}</strong>. It can not be edited
            or associated until approved.
          </Text>
        ),
      };

    case 'PendingApproval':
      if (isReviewer) {
        return {
          isVisible: true,
          title: <Text className={styles.thisIsADraftTitle}>Action required</Text>,
          content: (
            <Text className={styles.thisIsADraftMessage}>
              <strong>{submitter}</strong> submitted edits for approval. Please review changes.
            </Text>
          ),
        };
      }
      return {
        isVisible: true,
        title: <Text className={styles.thisIsADraftTitle}>Contract pending approval</Text>,
        content: (
          <Text className={styles.thisIsADraftMessage}>
            This contract is pending approval by <strong>{reviewer}</strong>. It can not be edited
            or associated until approved.
          </Text>
        ),
      };

    case 'Draft':
    case 'Paused':
      if (isOnboardedContract) {
        if (hasContractCreatePermission) {
          return {
            isVisible: true,
            title: <Text className={styles.thisIsADraftTitle}>Action required</Text>,
            content: (
              <Text className={styles.thisIsADraftMessage}>
                This contract is in a draft state. Please enter all required information to submit
                for approval.
              </Text>
            ),
          };
        }
        return {
          isVisible: true,
          title: <Text className={styles.thisIsADraftTitle}>This is a draft</Text>,
          content: (
            <Text className={styles.thisIsADraftMessage}>
              Contract will be available for review and approval once draft is submitted.
            </Text>
          ),
        };
      }
      if (hasContractCreatePermission) {
        return {
          isVisible: true,
          title: <Text className={styles.thisIsADraftTitle}>Action required</Text>,
          content: (
            <Text className={styles.thisIsADraftMessage}>
              <strong>{lastUpdatedBy}</strong> saved contract revisions as draft. Select Edit to
              continue making changes to contract draft and submit for review.
            </Text>
          ),
        };
      }
      return {
        isVisible: true,
        title: <Text className={styles.thisIsADraftTitle}>This is a draft</Text>,
        content: (
          <Text className={styles.thisIsADraftMessage}>
            <strong>{lastUpdatedBy}</strong> saved contract revisions as draft. Contract will be
            available for review and approval once draft revisions are submitted.
          </Text>
        ),
      };

    default: {
      return { isVisible: false };
    }
  }
};

const ContractDetailsMessageBar = ({
  contractStatus,
  isReviewer,
  hasContractCreatePermission,
  submitter,
  reviewer,
  reviewJustification,
  isOnboardedContract,
  lastUpdatedBy,
  latestActivity,
}) => {
  const message = getStatusMessage(
    contractStatus,
    isReviewer,
    hasContractCreatePermission,
    submitter,
    reviewer,
    isOnboardedContract,
    lastUpdatedBy,
  );

  const renderActivityDetails = () => {
    if (latestActivity.detailedDiff?.updated?.pendingUpdates) {
      return Object.keys(latestActivity.detailedDiff?.updated?.pendingUpdates).map(key => {
        if (['contractCurrency', 'paymentCurrency'].includes(key)) {
          return (
            <Text as="li" className={styles.thisIsADraftMessage}>
              {startCase(key)} change from{' '}
              <strong>{latestActivity.newData && latestActivity.newData[key]}</strong> to{' '}
              <strong>{latestActivity.newData?.pendingUpdates[key]}</strong> with effective date{' '}
              <strong>
                {formatDateString(latestActivity.newData?.pendingUpdates[`${key}EffectiveDate`])}
              </strong>
            </Text>
          );
        }
        return '';
      });
    }
    return '';
  };

  return (
    <>
      {message.isVisible && (
        <MessageBar isMultiline className={styles.messageBar}>
          {message.title}
          <br />
          {message.content}

          {/* Show reviewerJustification only to reviewer when he is about to approve/reject a contract */}
          {contractStatus !== 'Draft' && isReviewer && reviewJustification && (
            <ul className={styles.details}>
              <Text as="li" className={styles.thisIsADraftMessage}>
                Reviewer Justification: {reviewJustification}
              </Text>
              {latestActivity?.detailedDiff && renderActivityDetails()}
            </ul>
          )}
        </MessageBar>
      )}
    </>
  );
};

ContractDetailsMessageBar.propTypes = {
  contractStatus: PropTypes.string.isRequired,
  isReviewer: PropTypes.bool.isRequired,
  hasContractCreatePermission: PropTypes.bool.isRequired,
  submitter: PropTypes.string,
  reviewer: PropTypes.string,
  reviewJustification: PropTypes.string,
  isOnboardedContract: PropTypes.bool.isRequired,
  lastUpdatedBy: PropTypes.string,
  latestActivity: PropTypes.object,
};

ContractDetailsMessageBar.defaultProps = {
  submitter: '',
  reviewer: '',
  reviewJustification: '',
  lastUpdatedBy: '',
  latestActivity: null,
};

export default ContractDetailsMessageBar;
